/**
 * Cyber threat intelligence data source.
 * Aggregates threat data from multiple free feeds:
 *   - Feodo Tracker (botnet C2 servers)
 *   - URLhaus (malware distribution URLs)
 *   - CISA KEV (known exploited vulnerabilities)
 * No API key required for these feeds. CC0 1.0 / Public Domain.
 */

import type { Source, SourceMeta, ConfigField, SourceStatus, NormalizedEvent, Severity } from '../base-source';
import { djb2Hash } from '@/utils/hash';
import { getApiBase } from '@/app/api-router';

const SOURCE_ID = 'cyber';

// --- Feodo Tracker types ---

interface FeodoEntry {
  ip_address: string;
  port: number;
  status: string;
  hostname?: string;
  as_number?: number;
  as_name?: string;
  country?: string;
  first_seen?: string;
  last_online?: string;
  malware?: string;
}

// --- URLhaus types ---

interface UrlhausEntry {
  id: string;
  url: string;
  url_status: string;
  host?: string;
  threat?: string;
  tags?: string[];
  reporter?: string;
  date_added: string;
}

interface UrlhausResponse {
  urls?: UrlhausEntry[];
}

// --- CISA KEV types ---

interface CisaVuln {
  cveID: string;
  vendorProject: string;
  product: string;
  vulnerabilityName: string;
  dateAdded: string;
  shortDescription?: string;
  requiredAction?: string;
  dueDate?: string;
  knownRansomwareCampaignUse?: string;
}

interface CisaKevResponse {
  vulnerabilities?: CisaVuln[];
}

function malwareSeverity(malware: string | undefined): Severity {
  const lower = (malware ?? '').toLowerCase();
  if (lower.includes('emotet') || lower.includes('trickbot') || lower.includes('qakbot')) return 'critical';
  if (lower.includes('dridex') || lower.includes('pikabot')) return 'high';
  return 'medium';
}

let status: SourceStatus = {
  state: 'initializing',
  eventCount: 0,
};

/** Fetch a single cyber feed, returning empty array on failure. */
async function fetchFeed<T>(feed: string): Promise<T | null> {
  try {
    const url = `${getApiBase()}/api/data/cyber?feed=${feed}`;
    const response = await fetch(url, { signal: AbortSignal.timeout(15_000) });
    if (!response.ok) return null;
    return await response.json() as T;
  } catch {
    return null;
  }
}

export const cyberSource: Source = {
  id: SOURCE_ID,

  meta: {
    name: 'Cyber Threat Feeds',
    description: 'Aggregated cyber threat intelligence — Feodo, URLhaus, CISA KEV',
    icon: 'shield-alert',
    category: 'cyber',
    updateInterval: 300_000, // 5 min
    requiresApiKey: false,
    dataLicense: 'CC0 1.0 / Various',
    builtIn: true,
  } satisfies SourceMeta,

  configSchema: [] as ConfigField[],

  async initialize(): Promise<void> {
    status = { state: 'initializing', eventCount: 0 };
  },

  async start(): Promise<void> {
    status.state = 'active';
    console.log('[Sentinel] Cyber threat feed source started');
  },

  async stop(): Promise<void> {
    status.state = 'disabled';
  },

  async fetch(): Promise<NormalizedEvent[]> {
    try {
      // Fetch all feeds in parallel — resilient to individual failures
      const [feodoData, urlhausData, cisaData] = await Promise.allSettled([
        fetchFeed<FeodoEntry[]>('feodo'),
        fetchFeed<UrlhausResponse>('urlhaus'),
        fetchFeed<CisaKevResponse>('cisa-kev'),
      ]);

      const events: NormalizedEvent[] = [];
      const now = new Date().toISOString();

      // --- Process Feodo Tracker entries ---
      if (feodoData.status === 'fulfilled' && feodoData.value && Array.isArray(feodoData.value)) {
        // Limit to 200 most recent entries
        const feodoEntries = feodoData.value.slice(0, 200);
        for (const entry of feodoEntries) {
          if (!entry.ip_address) continue;

          events.push({
            id: `cyber-feodo-${djb2Hash(entry.ip_address)}`,
            source: SOURCE_ID,
            timestamp: entry.last_online ?? entry.first_seen ?? now,
            type: 'cyber_incident',
            title: `Botnet C2: ${entry.ip_address}:${entry.port} (${entry.malware ?? 'Unknown'})`,
            severity: malwareSeverity(entry.malware),
            tags: ['feodo-tracker', 'botnet', 'c2-server', entry.malware?.toLowerCase() ?? 'unknown'].filter(Boolean),
            raw: {
              feed: 'feodo-tracker',
              ip: entry.ip_address,
              port: entry.port,
              status: entry.status,
              malware: entry.malware,
              asNumber: entry.as_number,
              asName: entry.as_name,
              country: entry.country,
            },
          });
        }
      }

      // --- Process URLhaus entries ---
      if (urlhausData.status === 'fulfilled' && urlhausData.value?.urls) {
        // Limit to 100 most recent
        const urlEntries = urlhausData.value.urls.slice(0, 100);
        for (const entry of urlEntries) {
          if (!entry.url) continue;

          events.push({
            id: `cyber-urlhaus-${entry.id}`,
            source: SOURCE_ID,
            timestamp: entry.date_added ?? now,
            type: 'cyber_incident',
            title: `Malware URL: ${entry.host ?? entry.url.slice(0, 60)}`,
            severity: 'medium',
            tags: ['urlhaus', 'malware-url', entry.threat ?? 'unknown', ...(entry.tags ?? [])],
            links: [{ url: `https://urlhaus.abuse.ch/url/${entry.id}/`, label: 'URLhaus' }],
            raw: {
              feed: 'urlhaus',
              urlhausId: entry.id,
              url: entry.url,
              status: entry.url_status,
              threat: entry.threat,
              reporter: entry.reporter,
            },
          });
        }
      }

      // --- Process CISA KEV entries ---
      if (cisaData.status === 'fulfilled' && cisaData.value?.vulnerabilities) {
        // Only recent entries (last 30 days)
        const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const recentVulns = cisaData.value.vulnerabilities.filter(v => {
          const added = new Date(v.dateAdded).getTime();
          return added >= thirtyDaysAgo;
        });

        for (const vuln of recentVulns) {
          const isRansomware = vuln.knownRansomwareCampaignUse?.toLowerCase() === 'known';

          events.push({
            id: `cyber-kev-${vuln.cveID}`,
            source: SOURCE_ID,
            timestamp: vuln.dateAdded,
            type: 'cyber_incident',
            title: `Exploited Vuln: ${vuln.cveID} — ${vuln.vendorProject} ${vuln.product}`,
            description: vuln.shortDescription ?? vuln.vulnerabilityName,
            severity: isRansomware ? 'critical' : 'high',
            tags: ['cisa-kev', 'vulnerability', 'exploited', vuln.vendorProject.toLowerCase()],
            links: [{ url: `https://nvd.nist.gov/vuln/detail/${vuln.cveID}`, label: 'NVD' }],
            raw: {
              feed: 'cisa-kev',
              cveId: vuln.cveID,
              vendor: vuln.vendorProject,
              product: vuln.product,
              dueDate: vuln.dueDate,
              ransomware: isRansomware,
            },
          });
        }
      }

      status.state = 'active';
      status.lastFetch = now;
      status.eventCount = events.length;
      return events;
    } catch (err) {
      status.state = 'error';
      status.lastError = err instanceof Error ? err.message : String(err);
      throw err;
    }
  },

  getStatus(): SourceStatus {
    return { ...status };
  },
};
